import { FC, useMemo, useState } from "react";

type PageNumberPanelProps = {
  currentPage: number;
  totalPages: number;
  onPageChange: (pageNumber: number) => void;
};

export const PageNumberPanel: FC<PageNumberPanelProps> = ({
  currentPage,
  totalPages,
  onPageChange,
}) => {
  const [draftValue, setDraftValue] = useState<string | null>(null);

  const displayValue = draftValue ?? currentPage.toString();

  const inputWidth = useMemo(() => {
    const maxDigits = Math.max(
      totalPages.toString().length,
      displayValue.length
    );
    return Math.max(28, maxDigits * 8 + 20); // 20px for padding
  }, [totalPages, displayValue]);

  if (totalPages <= 1) {
    return null;
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/[^0-9]/g, "");
    setDraftValue(value);
  };

  const commitChange = () => {
    if (draftValue === null) {
      return;
    }

    const pageNum = parseInt(draftValue, 10);

    if (!isNaN(pageNum) && pageNum >= 1 && pageNum <= totalPages) {
      if (pageNum !== currentPage) {
        onPageChange(pageNum);
      }
    }

    setDraftValue(null);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      commitChange();
      (e.target as HTMLInputElement).blur();
    } else if (e.key === "Escape") {
      setDraftValue(null);
      (e.target as HTMLInputElement).blur();
    }
  };

  const handleBlur = () => {
    commitChange();
  };

  return (
    <div className="p-1 rounded-lg shadow-lg shadow-black/10 flex justify-center items-center bg-white w-auto text-zinc-500 pr-2">
      <div className="flex justify-center items-center gap-1">
        <div
          className="h-6 px-2 py-0.5 bg-white rounded-md border border-zinc-200 flex justify-center items-center overflow-hidden"
          style={{ width: `${inputWidth}px` }}
        >
          <input
            type="text"
            value={displayValue}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            onBlur={handleBlur}
            className="w-full text-center text-zinc-900 text-xs outline-none pointer-events-auto"
            aria-label="Current page"
          />
        </div>
        <div className="text-xs">/ {totalPages}</div>
      </div>
    </div>
  );
};
